import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';

import { bindAll } from '@bibliocommons/utils-react';
import { bibShape, ugcVideoShape } from '@bibliocommons/bc-prop-types';
import AccessibleTextArea from '@bibliocommons/deprecated-base-accessibility-text-area';
import ClearableTextField from '@bibliocommons/base-clearable-text-field';
import AccessibleTextField from '@bibliocommons/deprecated-base-accessibility-text-field';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { videoUtils } from '@bibliocommons/utils-ugc';
import { Film as FilmIcon } from '@bibliocommons/deprecated-base-icons';

import HelpLinkContainer from 'app/components/shared/HelpLink/HelpLinkContainer';
import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import VideoPlayback from '../../VideoPlayback';
import BaseUGCEditor from '../BaseUGCEditor';

import './VideoUGCEditor.scss';

const TITLE_CHARACTER_LIMIT = 50;
const DESC_CHARACTER_LIMIT = 500;
const URL_CHARACTER_LIMIT = 1000;
const URL_ERROR_KEY = 'url';
const TITLE_ERROR_KEY = 'title';

const { urlToIFrameSrc } = videoUtils;

function renderAsterisk() {
  return (
    <span className="required">
      *
      <ScreenReaderMessage>
        <FormattedMessage id="FIELD_REQUIRED" />
      </ScreenReaderMessage>
    </span>
  );
}

function renderPlaceHolder() {
  return (
    <div>
      <div className="placeholder" data-key="ugc-video-placeholder">
        <div className="icon-film">
          <FilmIcon />
        </div>
        <div className="instruction">
          <FormattedMessage id="video_embed_placeholder_info" />
        </div>
      </div>
    </div>
  );
}

export class VideoUGCEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    const { existingVideo } = this.props;
    this.state = {
      url: existingVideo ? existingVideo.get('url') : '',
      urlError: null,
      title: existingVideo ? existingVideo.get('title') : '',
      titleError: null,
      description: existingVideo ? existingVideo.get('description') : ''
    };
  }

  handleClear() {
    this.setState({ url: '', urlError: null });
  }

  handleUrlChange(value) {
    const urlError = value && !urlToIFrameSrc(value) ? { id: 'error_invalid_url' } : null;
    this.setState({ url: value, urlError });
  }

  handleDescriptionChange(value) {
    this.setState({ description: value });
  }

  handleTitleChange(value) {
    this.setState({ title: value, titleError: null });
  }

  handleSave(callback) {
    if (this.isFormValid()) {
      const { bib, existingVideo, videosActions } = this.props;
      const metadataId = bib.getIn(['briefInfo', 'metadataId']);
      const videoId = existingVideo && existingVideo.get('id');
      const url = this.state.url;
      const title = this.state.title;
      const description = this.state.description;
      const postSuccess = callback;

      if (videoId) {
        videosActions.updateVideo({
          metadataId,
          videoId,
          url,
          title,
          description,
          postSuccess
        });
      } else {
        videosActions.addVideo({
          metadataId,
          url,
          title,
          description,
          postSuccess
        });
      }
    } else {
      const errorState = {};

      if (!this.state.url) {
        errorState.urlError = { id: 'error_invalid_url' };
      }

      if (!this.state.title) {
        errorState.titleError = { id: 'error_title_required' };
      }

      if (!isEmpty(errorState)) {
        this.setState(errorState);
      }
    }
  }

  isFormValid() {
    const { urlError, titleError, url, title } = this.state;
    return url && title && !urlError && !titleError;
  }

  renderUrlInput() {
    const metadataId = this.props.bib.getIn(['briefInfo', 'metadataId']);

    const fieldErrors = this.state.urlError
      ? {
          [URL_ERROR_KEY]: {
            ...this.state.urlError,
            values: {
              url: (
                <HelpLinkContainer showIcon={false} page="025ug/020video/">
                  <FormattedMessage id="catalog_help" />
                </HelpLinkContainer>
              )
            }
          }
        }
      : {};

    return (
      <div>
        <label htmlFor={`editor-url-${metadataId}`} id={`label-url-${metadataId}`}>
          <FormattedMessage id="video_url_label" /> {renderAsterisk()}
        </label>
        <ClearableTextField
          id={`editor-url-${metadataId}`}
          className="editor-url"
          fieldErrors={fieldErrors}
          errorKey={URL_ERROR_KEY}
          handleChange={this.handleUrlChange}
          handleClear={this.handleClear}
          hideClearButton={!this.state.url}
          labelledById={`label-url-${metadataId}`}
          describedById={`editor-url-${metadataId}`}
          maxLength={URL_CHARACTER_LIMIT}
          characterCountSrOnly
          name="url"
          type="text"
          value={this.state.url}
          inputTestId="editor-url"
          info={<FormattedMessage id="video_url_info" />}
          required
        />
      </div>
    );
  }

  renderVideo() {
    const video =
      !this.state.urlError && this.state.url !== '' ? (
        <VideoPlayback src={urlToIFrameSrc(this.state.url)} title={this.state.title} />
      ) : (
        renderPlaceHolder()
      );

    return (
      <div>
        <div className="label-non-input">
          <FormattedMessage id="preview_label" />
        </div>
        {video}
      </div>
    );
  }

  renderTitleInput() {
    const metadataId = this.props.bib.getIn(['briefInfo', 'metadataId']);

    const fieldErrors = this.state.titleError
      ? {
          [TITLE_ERROR_KEY]: this.state.titleError
        }
      : {};

    return (
      <div>
        <label htmlFor={`editor-title-${metadataId}`} id={`label-title-${metadataId}`}>
          <FormattedMessage id="add_title_label" /> {renderAsterisk()}
        </label>
        <AccessibleTextField
          id={`editor-title-${metadataId}`}
          className="editor-title"
          fieldErrors={fieldErrors}
          errorKey={TITLE_ERROR_KEY}
          handleChange={this.handleTitleChange}
          labelledById={`label-title-${metadataId}`}
          describedById={`editor-title-${metadataId}`}
          name="title"
          type="text"
          value={this.state.title}
          maxLength={TITLE_CHARACTER_LIMIT}
          testId="editor-title"
          required
        />
      </div>
    );
  }

  renderDescription() {
    const metadataId = this.props.bib.getIn(['briefInfo', 'metadataId']);

    return (
      <div>
        <label htmlFor={`editor-description-${metadataId}`} id={`label-description-${metadataId}`}>
          <FormattedMessage id="add_description_label" />
        </label>
        <AccessibleTextArea
          id={`editor-description-${metadataId}`}
          required
          fitToText
          name="description"
          className="editor-textarea"
          maxLength={DESC_CHARACTER_LIMIT}
          labelledById={`label-description-${metadataId}`}
          handleChange={this.handleDescriptionChange}
          testId="editor-description"
          value={this.state.description}
        />
      </div>
    );
  }

  render() {
    const {
      bib,
      existingVideo,
      videosTransactions,
      shelfItem,
      shelfActions,
      shelfTransactions,
      handleClose
    } = this.props;
    const videoId = existingVideo && existingVideo.get('id');
    const metadataId = bib.getIn(['briefInfo', 'metadataId']);
    const renderHeader = () => (
      <h2>{videoId ? <FormattedMessage id="my_video" /> : <FormattedMessage id="add_a_video" />}</h2>
    );
    const isSavingVideo = videoId
      ? videosTransactions.getIn([metadataId, videoId, 'isUpdating'], false)
      : videosTransactions.getIn([metadataId, 'isAdding'], false);

    return (
      <BaseUGCEditor
        bib={bib}
        className="cp-video-ugc-editor"
        renderHeader={renderHeader}
        handleSave={this.handleSave}
        handleClose={handleClose}
        isSaving={isSavingVideo}
        showPrivacyAlert
        shelfItem={shelfItem}
        shelfTransactions={shelfTransactions}
        shelfActions={shelfActions}
      >
        <div className="content-blocks">
          <div className="left-block">
            {this.renderUrlInput()}
            {this.renderTitleInput()}
            {this.renderDescription()}
          </div>
          <div className="right-block">{this.renderVideo()}</div>
        </div>
      </BaseUGCEditor>
    );
  }
}

VideoUGCEditor.propTypes = {
  bib: bibShape.isRequired,
  existingVideo: ugcVideoShape,
  shelfItem: shelfItemShape,
  handleClose: PropTypes.func.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired
};

export default VideoUGCEditor;
